
import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react'
import { AuthenticateUser, getNewToken, useGetCurrentUser } from '../../graphql';
import SuccessAlert from '../../components/Alerts/SuccessAlert';
import FailAlert from '../../components/Alerts/FailAlert';
import useStore from "../../store/useStore";
import { Link, useNavigate } from 'react-router-dom';
import RegImg from '../../assets/registerImage.png'
import { t } from 'i18next';

interface Props {
  direction: string
}

const Login = ({ direction }: Props) => {
  const [getCurrentUserLazy] = useGetCurrentUser();

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    // Check if the user is logged in
    getNewToken()
      .then(() => {
        navigate("/home");
        return
      })
      .catch(() => {
        return
      })
  }, [])

  const [authenticateUser, { loading }] = useMutation(AuthenticateUser, {
    onError: (error) => {
      // @ts-ignore
      const code = error.networkError?.result?.errors?.[0]?.extensions?.code;

      if (code === "USER_NOT_EXIST") {
        setFail(true);
        setFailMsg(t('emailIncorrect'));
        return

      } else if (code === "PASSWORD_INCORRECT") {
        setFail(true);
        setFailMsg(t('passwordIncorrect'));
        return
      } else {
        console.error(code);
        setFail(true);
        setFailMsg(code);
      }
    },
    onCompleted: ({ authenticateUser }) => {

      useStore.setState({ token: authenticateUser?.token });
      useStore.setState({ isAuth: true });

      getCurrentUserLazy({
        fetchPolicy: "network-only",

        onCompleted: ({ currentUser }) => {
          setSuccess(true);
          setSuccessMsg(t('loginSuccess'));
          useStore.setState({ userData: currentUser });

          navigate("/home");
        }
      })
    }
  });

  const submit = async (e: any) => {
    e.preventDefault()

    if (email === "") {
      setFailMsg(t('emailEmpty'));
      setFail(true);
      return
    }
    if (password === "") {
      setFailMsg(t('passwordEmpty'));
      setFail(true);
      return
    }

    await authenticateUser({
      variables: {
        content: {
          email: email,
          password: password,
        }
      }
    })
  }

  return (
    <section>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      {
        loading &&
        <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
          <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
          </svg>
        </div>
      }

      <div>
        <div className={`hidden md:block md:fixed md:w-1/2 ${direction === 'ltr' ? 'md:float-left' : "md:float-right"}`}>
          <div className='relative w-full'>
            <img src={RegImg} className='hidden object-cover w-full h-screen md:block' alt="Register img" />
            <div className='absolute top-0 hidden w-full h-full px-6 py-5 text-white md:block bg-black/30'></div>
          </div>
        </div>

        <div className={`flex items-center justify-center min-h-screen md:w-1/2  ${direction === 'ltr' ? 'md:float-right' : "md:float-left"}`}>
          <div className='h-full py-3 md:py-10'>
            <form onSubmit={(e) => e.preventDefault()}>
              <h2 className='mb-6 text-4xl font-bold text-gray-800'>
                {t('loginToYourAccount')}
              </h2>

              <div className='mb-4'>
                <label className="block text-sm font-semibold text-gray-500">
                  {t('email')}
                </label>

                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 mt-2 text-gray-500 bg-transparent border rounded-lg shadow-sm outline-none focus:border-primary" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-500">
                  {t('password')}
                </label>

                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 mt-2 text-gray-500 bg-transparent border rounded-lg shadow-sm outline-none focus:border-primary" />
              </div>

              <Link to={"/forgotPassword"} className='pt-1.5 text-sm font-semibold text-primary cursor-pointer'>
                {t('Forget Password?')}
              </Link>

              <button onClick={(e) => { submit(e) }} className="w-full px-3 py-2.5 mt-6 font-bold text-white duration-300 rounded-lg bg-primary hover:bg-primaryHover">
                {t('login')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Login